/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 03.06.17.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";
import update from 'immutability-helper';
import Form from "../Form";
import SchemaRegistry from "../../../services/SchemaRegistry";
import Schema from "../../../Schema";
import {deepClone, deepEquals} from "../../../util";
import BasePageComponent from "./BasePageComponent";
import {PageAction} from "../../../services/PageAction/PageAction";
import {PageActionActions} from "../../../services/PageAction";
import DataProvider from "../../../DataProvider";
import ParamParser from "../../../ParamParser";

class SearchForm extends BasePageComponent {
    static propTypes = {
        params: PropTypes.shape({
            resource: PropTypes.string.isRequired
        }).isRequired,
        location: PropTypes.object.isRequired,
        schema: PropTypes.string.isRequired,
        navSchema: PropTypes.string.isRequired,
        navField: PropTypes.string.isRequired
    };

    static defaultProps = {};

    constructor(props) {
        super(props);
        let schema = this.getSchema(props);
        this.state = {
            schema: schema,
            formData: (props.location.query ? this.getFilterFromProps(props, schema) : {})
        };
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    /**
     *
     * @param {Object} props
     * @param {Schema} schema
     * @returns {Object}
     */
    getFilterFromProps(props, schema) {

        let navSchema = this.getNavSchema(props);
        let paramParser = new ParamParser(props.params, null);
        let query = _.extend(
            {},
            paramParser.replaceValues(navSchema.getDefaultFilter()),
            props.location.query,
            paramParser.replaceValues(navSchema.getFilter()));

        let filter = {};

        for (let k of _.keys(query)) {
            let parts = k.split("/");
            if (parts.length > 0) {
                let ff = filter;
                for (let i = 0; i < parts.length-1; i++) {
                    if (!ff[parts[i]]) ff[parts[i]] = {};
                    ff = ff[parts[i]];
                }
                ff[parts[parts.length-1]] = query[k];
            } else {
                filter[k] = query[k];
            }
        }
        // Transform filter according to schema:
        return this.fixFilterData(filter, schema.getJsonSchema());
        return filter;
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.params.resource !== nextProps.params.resource || this.props.schema !== nextProps.schema) {
            let schema = this.getSchema(nextProps);
            this.setState({
                schema: schema,
                formData: (nextProps.location.query ? this.getFilterFromProps(nextProps, schema) : {})
            });
        } else if (this.props.location.search !== nextProps.location.search) {
            this.setState({
                formData: (nextProps.location.query ? this.getFilterFromProps(nextProps, this.state.schema) : {})
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            nextProps.params.resource !== this.props.params.resource ||
            nextProps.schema !== this.props.schema ||
            nextState !== this.state ||
            !deepEquals(nextProps.location.query, this.props.location.query)
        );
    }

    getSchema(props) {
        return this.fixSchema(SchemaRegistry.getSchemaObject(props.params.parentResource, props.params.resource, props.schema));
    }

    /**
     *
     * @param {Schema} schema
     * @returns {Schema}
     */
    fixSchema(schema) {
        let schemaData = schema.getJsonSchema();
        // In case of search forms there is no required fields:
        if (schemaData.required) {
            schema.jsonSchema = _.omit(schemaData, 'required');
        }
        // Traverse UI schema and if there exists "ui:type" elements then change schema accordingly
        let fixedSchema = this.fixSchemaRecursive(schema.jsonSchema, schema.getUiSchema());
        if (fixedSchema != schema.jsonSchema) schema.jsonSchema = fixedSchema;
        return schema;
    }

    fixSchemaRecursive(jsonSchema, uiSchema) {
        if (!jsonSchema || !uiSchema) return jsonSchema;
        if (uiSchema["ui:type"] && uiSchema["ui:type"] !== jsonSchema.type) {
            switch (uiSchema["ui:type"]) {
                case "array":
                    if (jsonSchema.type === "object") throw new Error("Invalid UI Schema file, type change from " + jsonSchema.type + " is not supported");
                    jsonSchema = {
                        "type": "array",
                        "title": jsonSchema.title,
                        "titlePl": jsonSchema.titlePl,
                        "items": jsonSchema
                    };
                    break;
                default:
                    throw new Error("Invalid UI Schema file, type change to " + uiSchema["ui:type"] + " is not supported");
                    break;

            }
        } else {
            // Traverse into schema if needed:
            switch (jsonSchema.type) {
                case "object":
                    for (let key of Object.keys(jsonSchema.properties)) {
                        let newSchema = this.fixSchemaRecursive(jsonSchema.properties[key], uiSchema[key]);
                        if (newSchema !== jsonSchema.properties[key]) {
                            // Change schema element:
                            jsonSchema = update(jsonSchema, {properties: {[key]: {$set: newSchema}}});
                        }
                    }
                    break;
                case "array":
                    let newSchema = this.fixSchemaRecursive(jsonSchema.items, uiSchema["*"]);
                    if (newSchema != jsonSchema.items) {
                        // Change schema element:
                        jsonSchema = update(jsonSchema, {items: {$set: newSchema}});
                    }
                    break;
            }
        }
        return jsonSchema;
    }

    /**
     * Transform filter into data according to the schema
     * @param {Object} filter
     * @param {JsonSchemaData} jsonSchema
     * @returns {Object}
     */
    fixFilterData(filter, jsonSchema) {
        if (!jsonSchema) return filter;
        switch (jsonSchema.type) {
            case "object":
                for (let key of Object.keys(filter)) {
                    let subFilter = this.fixFilterData(filter[key], jsonSchema.properties[key]);
                    if (subFilter !== filter[key]) {
                        // Change filter item:
                        filter = update(filter, {[key]: {$set: subFilter}});
                    }
                }
                break;
            case "array":
                if (!_.isArray(filter)) {
                    if (_.isObject(filter)) {
                        if (filter["*"]) {
                            let subFilter = this.fixFilterData(filter["*"], jsonSchema.items);
                            if (subFilter !== filter["*"]) {
                                // Change filter item:
                                filter = update(filter, {"*": {$set: subFilter}});
                            }
                        }
                    }
                    else filter = [filter];
                }
                else {
                    for (let i = 0; i < filter.length; i++) {
                        let subFilter = this.fixFilterData(filter[i], jsonSchema.items);
                        if (subFilter !== filter[i]) {
                            // Change filter item:
                            filter = update(filter, {$splice: [[i, 1, subFilter]]});
                        }
                    }
                }
                break;
        }
        return filter;
    }

    onChange(formData) {
        this.setState({formData: deepClone(formData)});
    }

    onSubmit() {
        let actions = this.state.schema.getUiSchemaObject().getAction();
        if (!actions || actions.length == 0) return;

        let searchData = this.getSearchData();
        if (searchData == null) return;

        let action = new PageAction(actions[0]);
        action.params = this.props.params;
        action.data = searchData;
        PageActionActions.callAction(action);
    }

    getSearchData() {
        let dataProvider = new DataProvider(this.state.schema, this.state.formData);
        let fields = this.state.schema.getOrder();
        if (!fields) return null;
        let searchData = {};
        for (let field of fields) {
            let provider = dataProvider.getProvider(field);
            let valueMapping = provider.getSchema().getUiSchemaElement("valueMapping");
            if (valueMapping == null) {
                let value = provider.getData();
                if (value != null) searchData[field] = value;
            } else {
                let parentField = field.split("/");
                parentField.pop();
                for (let valueField of valueMapping) {
                    let searchField = [...parentField, valueField].join("/");
                    let value = dataProvider.getValue(searchField);
                    if (value != null) searchData[searchField] = value;
                }
            }
        }
        return searchData;
    }

    render() {
        return (
            <Form params={this.props.params} schema={this.state.schema} formData={this.state.formData} onChange={this.onChange} onSubmit={this.onSubmit} location={this.props.location}/>
        );
    }
}

export default SearchForm;
